/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import InboundCallsWidget from "../widgets/call-overview/InboundCallsWidget";
import InboundDirectCallsWidget from "../widgets/call-overview/InboundDirectCallsWidget.tsx";
import InternalCallsWidget from "../widgets/call-overview/InternalCallsWidget";
import OutboundCallsWidget from "../widgets/call-overview/OutboundCallsWidget";
import AbandonedRateWidget from "../widgets/call-overview/AbandonedRateWidget";
import TransferredRateWidget from "../widgets/call-overview/TransferredRateWidget";
import CallVolumeGroupWidget from "../widgets/call-overview/CallVolumeGroupWidget.tsx";
import { DateRangeFilter, DateRangeFilterChange } from "../filters/DateRangeFilter.tsx";
import { DATE_RANGE_KEYS, calculateDateRange, getDistributionRangeType, getRecalculatedDatesIfNeeded } from "@/utils/dateRangeUtils";
import { WIDGET_NAMES } from "@/constants/widget";
import CallStatusDistributionTrendWidget from "../widgets/key-metrics/CallStatusDistributionTrendWidget";
import { WidgetType } from "@/types";
import { fetchKeyMetricsBulkRealtime } from "@/lib/axios/api/widgets.api";
import SLAWidget from "../widgets/call-overview/SLAWidget.tsx";
import TotalMissedCallsWidget from "../widgets/call-overview/TotalMissedCallsWidget.tsx";
import StickyFilterBar from "../common/StickyFilterBar";
import { useAppData, UserRole } from "@/contexts/AppDataContext";
import { EMPTY_AGENT_ID, EMPTY_QUEUE_ID } from "@/constants/queue";

interface KeyMetricsTabProps {
  isGeneralMode: boolean;
  filterClass?: string;
}


type KPI<T extends string, V extends string> = { [K in T]?: { [KV in V]: number } & { percentage: number | null } | null };
type CallVolumeRow = { time: Date; inboundCall: number; outboundCall: number; inboundDirectCall?: number; internalCall?: number };
type KeyMetricsPartial =
  KPI<"totalInbound", "totalInboundCalls">
  & KPI<"totalOutbound", "totalOutboundCalls">
  & KPI<"totalInboundDirect", "totalInboundDirectCalls">
  & KPI<"totalInternal", "totalInternalCalls">
  & KPI<"totalMissed", "totalMissedCalls">
  & { sla?: { slaRate: number; percentage: number | null } | null }
  & { callAbandoned?: { callAbandonedRate: number; percentage: number | null } | null }
  & { transferred?: { transferredRate: number; percentage: number | null } | null }
  & { callVolume?: CallVolumeRow[] }
  & { callStatusDistributionTrend?: unknown[] };

const INITIAL_WIDGET_LOADING: Record<string, boolean> = [
  WIDGET_NAMES.TOTAL_INBOUND_CALLS,
  WIDGET_NAMES.TOTAL_OUTBOUND_CALLS,
  WIDGET_NAMES.TOTAL_INBOUND_DIRECT_CALLS,
  WIDGET_NAMES.TOTAL_INTERNAL_CALLS,
  WIDGET_NAMES.TOTAL_MISSED_CALLS,
  WIDGET_NAMES.SLA,
  WIDGET_NAMES.CALL_ABANDONED_RATE,
  WIDGET_NAMES.TRANSFERRED_RATE,
  WIDGET_NAMES.CALL_VOLUME,
  WIDGET_NAMES.CALL_STATUS_DISTRIBUTION_TREND,
].reduce((acc, key) => {
  acc[key] = true;
  return acc;
}, {} as Record<string, boolean>);

// Function to get current date range - recalculates dynamic ranges to ensure endtime is current
const getInitialDateRange = (key: string = DATE_RANGE_KEYS.LAST_7_DAYS): DateRangeFilterChange => {
  const calculated = calculateDateRange(key);
  return {
    key,
    startDate: calculated.from,
    endDate: calculated.to,
    previousStartDate: calculated.previousFrom,
    previousEndDate: calculated.previousTo
  };
};

let kmDateRange: DateRangeFilterChange = getInitialDateRange();

const KeyMetricsTab: React.FC<KeyMetricsTabProps> = ({ isGeneralMode, filterClass }) => {
  // Recalculate date range on mount so dynamic ranges (Today, Last 7 days...) always carry the current endtime
  const [dateRange, setDateRange] = React.useState<DateRangeFilterChange>(() => {
    const recalculated = getRecalculatedDatesIfNeeded(kmDateRange.key, {
      startDate: kmDateRange.startDate.toISOString(),
      endDate: kmDateRange.endDate.toISOString(),
      previousStartDate: kmDateRange.previousStartDate.toISOString(),
      previousEndDate: kmDateRange.previousEndDate.toISOString(),
    });
    const fresh: DateRangeFilterChange = {
      key: kmDateRange.key,
      startDate: new Date(recalculated.startDate),
      endDate: new Date(recalculated.endDate),
      previousStartDate: new Date(recalculated.previousStartDate),
      previousEndDate: new Date(recalculated.previousEndDate),
    };
    kmDateRange = fresh;
    return fresh;
  });
  const updateDateRange = (value: DateRangeFilterChange) => {
    setDateRange(value);
    kmDateRange = value;
  };
  const [data, fetchData] = React.useState<KeyMetricsPartial>({});
  const [loadingMap, setLoadingMap] = React.useState<Record<string, boolean>>(INITIAL_WIDGET_LOADING);
  const [errorMap, setErrorMap] = React.useState<Record<string, string | null>>({});
  const [timeStamp, setTimeStamp] = React.useState(() => new Date().toISOString());
  const { queuesDataVersion, userRole, queueMicrosoftIds, uniqueAgents } = useAppData();

  // TCX-6501: for a Manager, scope every Key Metrics widget to their assigned call queues and the
  // agents in those queues/groups (uniqueAgents already covers both — AppDataContext dedupes across
  // all queue/group membership). directAgentIds mirrors agentIds so Direct Call widgets get the same
  // scope, including the EMPTY_AGENT_ID sentinel when the Manager has no visible agents.
  // Admin/Partner keeps sending [] for every field, exactly as before — this role check is the only
  // thing gating the new behavior.
  const roleScopedIds = React.useMemo(() => {
    if (userRole !== UserRole.MANAGER) {
      return { queueIds: [] as string[], agentIds: [] as string[], directAgentIds: [] as string[] };
    }
    const agentIds = uniqueAgents.length > 0 ? uniqueAgents.map(a => a.microsoftId) : [EMPTY_AGENT_ID];
    return {
      queueIds: queueMicrosoftIds.length > 0 ? queueMicrosoftIds : [EMPTY_QUEUE_ID],
      agentIds,
      directAgentIds: agentIds,
    };
  }, [userRole, queueMicrosoftIds, uniqueAgents]);

  // Refresh data when tab becomes active, or when with-agents-and-role poll detects a real
  // change (skip initial mount — timeStamp already has a fresh value)
  const isFirstActiveRef = React.useRef(true);
  React.useEffect(() => {
    if (isFirstActiveRef.current) {
      isFirstActiveRef.current = false;
      return;
    }
    if (isGeneralMode) {
      setTimeStamp(new Date().toISOString());
    }
  }, [isGeneralMode, queuesDataVersion]);

  React.useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const offsetInMinutes = -new Date().getTimezoneOffset();
    const basePayload = {
      start: dateRange.startDate.toISOString(),
      end: dateRange.endDate.toISOString(),
      previousStart: dateRange.previousStartDate.toISOString(),
      previousEnd: dateRange.previousEndDate.toISOString(),
      offsetInMinutes,
      queueIds: roleScopedIds.queueIds,
      agentIds: roleScopedIds.agentIds,
      directAgentIds: roleScopedIds.directAgentIds,
    };

    const setL = (k: string, v: boolean) => isMounted && setLoadingMap(prev => ({ ...prev, [k]: v }));
    const setE = (k: string, v: string | null) => isMounted && setErrorMap(prev => ({ ...prev, [k]: v }));
    const setD = (key: string, value: unknown) => isMounted && fetchData((prev) => ({ ...prev, [key]: value } as KeyMetricsPartial));

    // New API widget IDs: 1-10 (10 = Call Status Distribution Trend, folded in per TCX-6286)
    const mapWidgetToKey: Record<number, string> = {
      1: "totalInbound",
      2: "totalOutbound",
      3: "totalMissed",
      4: "totalInternal",
      5: "transferred",
      6: "sla",
      7: "callAbandoned",
      8: "callVolume",
      9: "totalInboundDirect",
      10: "callStatusDistributionTrend",
    };

    const bulkWidgets = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const bulkNames = [
      WIDGET_NAMES.TOTAL_INBOUND_CALLS,
      WIDGET_NAMES.TOTAL_OUTBOUND_CALLS,
      WIDGET_NAMES.TOTAL_INBOUND_DIRECT_CALLS,
      WIDGET_NAMES.TOTAL_INTERNAL_CALLS,
      WIDGET_NAMES.TOTAL_MISSED_CALLS,
      WIDGET_NAMES.SLA,
      WIDGET_NAMES.CALL_ABANDONED_RATE,
      WIDGET_NAMES.TRANSFERRED_RATE,
      WIDGET_NAMES.CALL_VOLUME,
      WIDGET_NAMES.CALL_STATUS_DISTRIBUTION_TREND,
    ];

    bulkNames.forEach(n => setL(n, true));
    bulkNames.forEach(n => setE(n, null));

    (async () => {
      try {
        const bulkRes = await fetchKeyMetricsBulkRealtime(basePayload, bulkWidgets, { signal: controller.signal });
        Object.entries(bulkRes || {}).forEach(([k, v]) => {
          const dest = mapWidgetToKey[Number(k)];
          if (dest) setD(dest, v);
        });
      } catch (err: any) {
        if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
          bulkNames.forEach(n => setE(n, err?.message || "Failed to load data"));
        }
      } finally {
        bulkNames.forEach(n => setL(n, false));
      }
    })();
    return () => { isMounted = false; controller.abort(); };
  }, [dateRange, timeStamp, roleScopedIds]);

  const renderWidget = (widgetId: string, WidgetComponent: React.ComponentType<any>, data?: any) => {
    return (
      <WidgetComponent
        key={widgetId}
        data={{
          ...data
        }}
        isGeneralMode={true}
        loading={!!loadingMap[widgetId]}
        error={errorMap[widgetId] || null}
      />
    );
  };

  if (!isGeneralMode) return null;

  return (
    <div className="w-full">
      <div className="border text-primary bg-[#FCFCFD] rounded-lg dark:border-[#3C5C73] dark:bg-[#3C5C73] dark:text-white px-[8px] pb-[8px] pt-1">
        <StickyFilterBar className={filterClass}>
          <div className="mb-4 flex flex-col md:flex-row align-center gap-2">
            <DateRangeFilter
              value={dateRange.key}
              onChange={updateDateRange}
              initialDateRange={dateRange as any}
            />
          </div>
        </StickyFilterBar>

        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {renderWidget(WIDGET_NAMES.TOTAL_INBOUND_CALLS, InboundCallsWidget, {
            value: data?.totalInbound?.totalInboundCalls,
            comparisonPercentage: data?.totalInbound?.percentage
          })}
          {renderWidget(WIDGET_NAMES.TOTAL_OUTBOUND_CALLS, OutboundCallsWidget, {
            value: data?.totalOutbound?.totalOutboundCalls,
            comparisonPercentage: data?.totalOutbound?.percentage
          })}
          {renderWidget(WIDGET_NAMES.TOTAL_INBOUND_DIRECT_CALLS, InboundDirectCallsWidget, {
            value: data?.totalInboundDirect?.totalInboundDirectCalls,
            comparisonPercentage: data?.totalInboundDirect?.percentage
          })}
          {renderWidget(WIDGET_NAMES.TOTAL_INTERNAL_CALLS, InternalCallsWidget, {
            value: data?.totalInternal?.totalInternalCalls,
            comparisonPercentage: data?.totalInternal?.percentage
          })}
          {renderWidget(WIDGET_NAMES.TOTAL_MISSED_CALLS, TotalMissedCallsWidget, {
            value: data?.totalMissed?.totalMissedCalls,
            comparisonPercentage: data?.totalMissed?.percentage
          })}
          {renderWidget(WIDGET_NAMES.SLA, SLAWidget, {
            value: data?.sla?.slaRate,
            comparisonPercentage: data?.sla?.percentage
          })}
          {renderWidget(WIDGET_NAMES.CALL_ABANDONED_RATE, AbandonedRateWidget, {
            value: data?.callAbandoned?.callAbandonedRate,
            comparisonPercentage: data?.callAbandoned?.percentage
          })}
          {renderWidget(WIDGET_NAMES.TRANSFERRED_RATE, TransferredRateWidget, {
            value: data?.transferred?.transferredRate,
            comparisonPercentage: data?.transferred?.percentage
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4" style={{ minHeight: `calc(100vh - 460px)` }}>
          <CallVolumeGroupWidget
            data={data?.callVolume || []}
            dateRange={{ from: new Date(dateRange.startDate), to: new Date(dateRange.endDate) }}
            loading={!!loadingMap[WIDGET_NAMES.CALL_VOLUME]}
          />
          <CallStatusDistributionTrendWidget
            data={{
              id: "call-status-distribution-trend",
              type: WidgetType.INBOUND_STATUS_DISTRIBUTION,
              title: "Call Status Distribution Trend",
              value: data?.callStatusDistributionTrend || [],
            }}
            loading={!!loadingMap[WIDGET_NAMES.CALL_STATUS_DISTRIBUTION_TREND]}
            error={errorMap[WIDGET_NAMES.CALL_STATUS_DISTRIBUTION_TREND] || null}
            dateRange={{ from: new Date(dateRange.startDate), to: new Date(dateRange.endDate) }}
            rangeType={getDistributionRangeType(dateRange.startDate, dateRange.endDate)}
          />
        </div>
      </div>
    </div>
  );
};

export default KeyMetricsTab;
